/**
 * Pure configuration helpers for the browser OpenTelemetry (SigNoz) setup. Kept free of SDK
 * imports so they can be unit tested without a browser.
 */

export const DEFAULT_OTEL_SERVICE_NAME = 'nonce-dashboard-v2-client';

/** The subset of `import.meta.env` the OpenTelemetry setup reads. */
export interface OtelEnv {
  PROD?: boolean;
  VITE_OTEL_ENABLED?: string;
  VITE_OTEL_EXPORTER_OTLP_ENDPOINT?: string;
  VITE_OTEL_EXPORTER_OTLP_HEADERS?: string;
  VITE_OTEL_SERVICE_NAME?: string;
  VITE_OTEL_ENVIRONMENT?: string;
  VITE_OTEL_TRACES_SAMPLE_RATE?: string;
  VITE_APP_VERSION?: string;
}

export interface OtelConfig {
  /** Collector base URL without a trailing slash, e.g. `/api/otel` or `https://otel.example.com`. */
  endpoint: string;
  serviceName: string;
  environment: string;
  serviceVersion: string;
  headers: Record<string, string>;
  tracesSampleRate: number;
}

/**
 * OpenTelemetry only runs when a collector endpoint is configured, and then:
 * - `VITE_OTEL_ENABLED=true` turns it on (use this to try it in local development),
 * - `VITE_OTEL_ENABLED=false` turns it off,
 * - otherwise it is on for production builds only, so `vite` dev servers stay quiet by default.
 */
export const isOtelEnabled = (env: OtelEnv): boolean => {
  if (!env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT?.trim()) {
    return false;
  }

  if (env.VITE_OTEL_ENABLED === 'true') {
    return true;
  }

  if (env.VITE_OTEL_ENABLED === 'false') {
    return false;
  }

  return Boolean(env.PROD);
};

/**
 * Parses headers in the standard `OTEL_EXPORTER_OTLP_HEADERS` format: `key1=value1,key2=value2`,
 * with URL-encoded values. Malformed entries are skipped.
 */
export const parseOtlpHeaders = (raw?: string): Record<string, string> => {
  const headers: Record<string, string> = {};

  for (const entry of (raw ?? '').split(',')) {
    const separatorIndex = entry.indexOf('=');
    if (separatorIndex <= 0) {
      continue;
    }

    const key = entry.slice(0, separatorIndex).trim();
    const value = entry.slice(separatorIndex + 1).trim();
    if (!key || !value) {
      continue;
    }

    try {
      headers[key] = decodeURIComponent(value);
    } catch {
      headers[key] = value;
    }
  }

  return headers;
};

/** Matches Sentry's default trace sample rate on this app (`VITE_SENTRY_TRACES_SAMPLE_RATE`). */
export const DEFAULT_TRACES_SAMPLE_RATE = 0.2;

/** Sample rate between 0 and 1; anything missing or out of range falls back to the default. */
export const parseSampleRate = (raw?: string): number => {
  const rate = Number.parseFloat(raw ?? '');
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : DEFAULT_TRACES_SAMPLE_RATE;
};

/**
 * Resolves the OpenTelemetry config, or `null` when it is disabled. The same Docker image is
 * deployed to testnet and mainnet, so when `VITE_OTEL_ENVIRONMENT` is not set the environment is
 * worked out at runtime from the hostname.
 */
export const getOtelConfig = (env: OtelEnv, isTestnet: boolean): OtelConfig | null => {
  if (!isOtelEnabled(env)) {
    return null;
  }

  return {
    endpoint: (env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT ?? '').trim().replace(/\/+$/, ''),
    serviceName: env.VITE_OTEL_SERVICE_NAME?.trim() || DEFAULT_OTEL_SERVICE_NAME,
    environment: env.VITE_OTEL_ENVIRONMENT?.trim() || (isTestnet ? 'Testnet' : 'Production'),
    serviceVersion: env.VITE_APP_VERSION?.trim() || 'unknown',
    headers: parseOtlpHeaders(env.VITE_OTEL_EXPORTER_OTLP_HEADERS),
    tracesSampleRate: parseSampleRate(env.VITE_OTEL_TRACES_SAMPLE_RATE),
  };
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Builds the `ignoreUrls` pattern for the fetch/XHR instrumentations: it matches every URL that
 * is NOT a same-origin `/api` call, so only proxy requests get spans. Same-origin requests are the
 * only ones the instrumentations add `traceparent` to (we never pass `propagateTraceHeaderCorsUrls`),
 * so third-party calls (Auth0, Google Maps, the collector itself) are left untouched.
 */
export const createNonApiUrlPattern = (origin: string): RegExp =>
  new RegExp(`^(?!${escapeRegExp(origin)}/api(?:[/?#]|$))`);

/**
 * Resolves the collector endpoint against the page origin. The recommended endpoint is the
 * same-origin `/api/otel` route on our Express server, but the OTLP exporters need absolute URLs.
 */
export const resolveOtelEndpoint = (endpoint: string, origin: string): string =>
  new URL(endpoint, origin).href.replace(/\/+$/, '');

/**
 * Matches the exporter's own requests, so that when the endpoint is the same-origin `/api/otel`
 * route the exports don't get traced (which would trace the exports of those spans, and so on).
 */
export const createExportUrlPattern = (resolvedEndpoint: string): RegExp =>
  new RegExp(`^${escapeRegExp(resolvedEndpoint)}(?:[/?#]|$)`);

const REDACTED = ':redacted';
const ID_SEGMENT = ':id';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a path segment looks like an identifier or personal data rather than a route name:
 * numbers (customer, machine, ticket ids), UUIDs, anything with an `@` (emails), and long tokens
 * containing a digit (wallet addresses, transaction hashes, phone numbers).
 */
const isIdentifierSegment = (segment: string) => {
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // keep the raw segment
  }

  return (
    /^\d+$/.test(decoded) ||
    UUID_PATTERN.test(decoded) ||
    decoded.includes('@') ||
    (decoded.length >= 12 && /\d/.test(decoded))
  );
};

/** Replaces identifier-like segments of a path, e.g. `/customers/123/notes` -> `/customers/:id/notes`. */
export const redactPath = (path: string): string =>
  path
    .split('/')
    .map((segment) => (segment && isIdentifierSegment(segment) ? ID_SEGMENT : segment))
    .join('/');

/**
 * Redacts a URL or path before it is exported: identifier-like path segments are replaced (see
 * `redactPath`), query parameter values are dropped (names are kept, they are useful for grouping)
 * and the fragment is removed.
 */
export const redactUrl = (url: string): string => {
  const hasOrigin = /^[a-z][a-z\d+.-]*:\/\//i.test(url);
  let parsed: URL;
  try {
    parsed = new URL(url, 'http://placeholder.invalid');
  } catch {
    return redactPath(url.split(/[?#]/)[0]);
  }

  const query = [...parsed.searchParams.keys()].map((key) => `${key}=${REDACTED}`).join('&');
  const pathAndQuery = `${redactPath(parsed.pathname)}${query ? `?${query}` : ''}`;

  return hasOrigin ? `${parsed.origin}${pathAndQuery}` : pathAndQuery;
};

/** Span attributes that carry a URL or path and are redacted before export. */
export const URL_ATTRIBUTE_KEYS = [
  'url.full',
  'url.path',
  'url.query',
  'http.url',
  'http.target',
  'app.route.previous_path',
];

/** Returns a copy of the attributes with every URL/path attribute redacted. */
export const redactUrlAttributes = <T extends Record<string, unknown>>(attributes: T): T => {
  const redacted: Record<string, unknown> = { ...attributes };

  for (const key of URL_ATTRIBUTE_KEYS) {
    const value = redacted[key];
    if (typeof value !== 'string') {
      continue;
    }
    redacted[key] = key === 'url.query' ? REDACTED : redactUrl(value);
  }

  return redacted as T;
};
